import React, { useState } from 'react';
import { Layout as RALayout, Menu, MenuItemLink, usePermissions, useLogout, AppBar, TitlePortal, useSidebarState } from 'react-admin';
import {
  LayoutDashboard,
  ShoppingBag,
  Layers,
  ShoppingCart,
  Users,
  MessageSquare,
  Ticket,
  Image as ImageIcon,
  Megaphone,
  HelpCircle,
  CircleHelp,
  Handshake,
  BarChart3,
  Search,
  PackageCheck,
  Star,
  Clock,
  LogOut,
  Settings,
  Truck,
  Award
} from 'lucide-react';
import { Box, Divider, Button } from '@mui/material';

const CustomAppBar = () => (
  <AppBar
    sx={{
      '& .RaAppBar-toolbar': {
        backgroundColor: '#7A68A7',
        color: '#FFFFFF',
        minHeight: { xs: '56px', sm: '64px' },
        paddingX: { xs: 1, sm: 2 },
      },
      '& .RaAppBar-title': {
        flex: 1,
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        fontSize: { xs: '1rem', sm: '1.25rem' },
        fontWeight: 600,
      },
      // 데스크탑에서 햄버거 버튼 숨김
      '& .RaAppBar-menuButton': {
        display: { xs: 'block', md: 'none' },
      },
    }}
  >
    <TitlePortal />
  </AppBar>
);

const CustomMenu = () => {
  const { permissions } = usePermissions();
  const [open] = useSidebarState();
  const logout = useLogout();
  const [menuState, setMenuState] = useState({
    catalog: true,
    sales: true,
    customers: true,
    marketing: true,
    analytics: true,
    settings: true,
  });

  const handleToggle = (section: string) => {
    setMenuState(prev => ({
      ...prev,
      [section]: !prev[section as keyof typeof prev],
    }));
  };

  const isAdmin = permissions?.role === 'SUPER_ADMIN' || permissions?.role === 'ADMIN';

  return (
    <Menu>
      <MenuItemLink
        to="/"
        primaryText="대시보드"
        leftIcon={<LayoutDashboard size={20} />}
      />

      <Divider sx={{ my: 1 }} />

      {/* 상품 관리 섹션 */}
      {open && (
        <Box sx={{ px: 2, py: 0.5 }}>
          <Box
            onClick={() => handleToggle('catalog')}
            sx={{
              cursor: 'pointer',
              fontWeight: 600,
              fontSize: '0.875rem',
              color: 'text.secondary',
              mb: 1
            }}
          >
            상품 관리
          </Box>
        </Box>
      )}
      {(open ? menuState.catalog : true) && (
        <>
          <MenuItemLink
            to="/products"
            primaryText="상품 목록"
            leftIcon={<ShoppingBag size={20} />}
          />
          <MenuItemLink
            to="/categories"
            primaryText="카테고리"
            leftIcon={<Layers size={20} />}
          />
          <MenuItemLink
            to="/featured-products"
            primaryText="MD'S PICK"
            leftIcon={<Star size={20} />}
          />
          <MenuItemLink
            to="/deal-featured"
            primaryText="캣츄딜"
            leftIcon={<Clock size={20} />}
          />
          {isAdmin && (
            <MenuItemLink
              to="/inventory"
              primaryText="재고 관리"
              leftIcon={<PackageCheck size={20} />}
            />
          )}
        </>
      )}

      <Divider sx={{ my: 1 }} />

      {/* 주문 관리 섹션 */}
      {open && (
        <Box sx={{ px: 2, py: 0.5 }}>
          <Box
            onClick={() => handleToggle('sales')}
            sx={{
              cursor: 'pointer',
              fontWeight: 600,
              fontSize: '0.875rem',
              color: 'text.secondary',
              mb: 1
            }}
          >
            주문 관리
          </Box>
        </Box>
      )}
      {(open ? menuState.sales : true) && (
        <>
          <MenuItemLink
            to="/orders"
            primaryText="주문 목록"
            leftIcon={<ShoppingCart size={20} />}
          />
          <MenuItemLink
            to="/order-search"
            primaryText="주문 검색"
            leftIcon={<Search size={20} />}
          />
        </>
      )}

      <Divider sx={{ my: 1 }} />

      {/* 고객 관리 섹션 */}
      {open && (
        <Box sx={{ px: 2, py: 0.5 }}>
          <Box
            onClick={() => handleToggle('customers')}
            sx={{
              cursor: 'pointer',
              fontWeight: 600,
              fontSize: '0.875rem',
              color: 'text.secondary',
              mb: 1
            }}
          >
            고객 관리
          </Box>
        </Box>
      )}
      {(open ? menuState.customers : true) && (
        <>
          <MenuItemLink
            to="/users"
            primaryText="회원"
            leftIcon={<Users size={20} />}
          />
          <MenuItemLink
            to="/reviews"
            primaryText="리뷰"
            leftIcon={<MessageSquare size={20} />}
          />
          <MenuItemLink
            to="/inquiries"
            primaryText="문의"
            leftIcon={<HelpCircle size={20} />}
          />
          <MenuItemLink
            to="/faqs"
            primaryText="자주 묻는 질문"
            leftIcon={<CircleHelp size={20} />}
          />
          <MenuItemLink
            to="/partnership-inquiries"
            primaryText="입점/제휴 문의"
            leftIcon={<Handshake size={20} />}
          />
        </>
      )}

      <Divider sx={{ my: 1 }} />

      {/* 마케팅 섹션 */}
      {open && (
        <Box sx={{ px: 2, py: 0.5 }}>
          <Box
            onClick={() => handleToggle('marketing')}
            sx={{
              cursor: 'pointer',
              fontWeight: 600,
              fontSize: '0.875rem',
              color: 'text.secondary',
              mb: 1
            }}
          >
            마케팅 관리
          </Box>
        </Box>
      )}
      {(open ? menuState.marketing : true) && (
        <>
          <MenuItemLink
            to="/coupons"
            primaryText="쿠폰"
            leftIcon={<Ticket size={20} />}
          />
          <MenuItemLink
            to="/banners"
            primaryText="배너"
            leftIcon={<ImageIcon size={20} />}
          />
          <MenuItemLink
            to="/notices"
            primaryText="공지사항"
            leftIcon={<Megaphone size={20} />}
          />
        </>
      )}

      {isAdmin && (
        <>
          <Divider sx={{ my: 1 }} />

          {/* 분석 섹션 */}
          {open && (
            <Box sx={{ px: 2, py: 0.5 }}>
              <Box
                onClick={() => handleToggle('analytics')}
                sx={{
                  cursor: 'pointer',
                  fontWeight: 600,
                  fontSize: '0.875rem',
                  color: 'text.secondary',
                  mb: 1
                }}
              >
                분석 & 리포트
              </Box>
            </Box>
          )}
          {(open ? menuState.analytics : true) && (
            <>
              <MenuItemLink
                to="/reports/sales"
                primaryText="매출 리포트"
                leftIcon={<BarChart3 size={20} />}
              />
              <MenuItemLink
                to="/reports/products"
                primaryText="상품 실적"
                leftIcon={<BarChart3 size={20} />}
              />
              <MenuItemLink
                to="/reports/customers"
                primaryText="고객 분석"
                leftIcon={<BarChart3 size={20} />}
              />
            </>
          )}
        </>
      )}

      <Divider sx={{ my: 1 }} />

      {/* 설정 섹션 */}
      {open && (
        <Box sx={{ px: 2, py: 0.5 }}>
          <Box
            onClick={() => handleToggle('settings')}
            sx={{
              cursor: 'pointer',
              fontWeight: 600,
              fontSize: '0.875rem',
              color: 'text.secondary',
              mb: 1
            }}
          >
            설정
          </Box>
        </Box>
      )}
      {(open ? menuState.settings : true) && (
        <>
          <MenuItemLink
            to="/shop-settings"
            primaryText="배송/환불 설정"
            leftIcon={<Truck size={20} />}
          />
        </>
      )}

      {/* 로그아웃 버튼 */}
      <Box sx={{ mt: 3 }}>
        <Divider sx={{ mb: 1 }} />
        <Button
          onClick={() => logout()}
          startIcon={<LogOut size={20} />}
          fullWidth
          sx={{
            justifyContent: open ? 'flex-start' : 'center',
            px: 2,
            py: 1,
            mx: open ? 1 : 0,
            minWidth: open ? 'auto' : '40px',
            color: 'text.secondary',
            '&:hover': {
              backgroundColor: '#FEE2E2',
              color: '#DC2626',
            },
            '& .MuiButton-startIcon': {
              margin: open ? '0 8px 0 -4px' : 0,
            },
            '& .MuiButton-endIcon': {
              display: open ? 'inherit' : 'none',
            },
            '& .MuiButton-label': {
              display: open ? 'block' : 'none',
            }
          }}
        >
          {open && "로그아웃"}
        </Button>
      </Box>
    </Menu>
  );
};

const CustomLayout = (props: any) => {
  const [, setOpen] = useSidebarState();
  // 데스크탑 여부 확인 (md 이상)
  // Note: We can't use useMediaQuery here directly inside the component if we want to avoid re-renders or complexity, 
  // but for this requirement, checking window width or using CSS is safer. 
  // However, react-admin persists sidebar state. We want to ENFORCE it.

  // Effect to force sidebar open on desktop
  React.useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 900) { // 900px is default md breakpoint
        setOpen(true);
      }
    };

    // Initial check
    handleResize();

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [setOpen]);

  return (
    <RALayout
      {...props}
      appBar={CustomAppBar}
      menu={CustomMenu}
      sx={{
        '& .RaLayout-root': {
          backgroundColor: '#FFFFFF',
        },
        '& .RaLayout-appFrame': {
          marginTop: { xs: '56px', sm: '64px' },
          backgroundColor: '#FFFFFF',
        },
        '& .RaLayout-contentWithSidebar': {
          marginTop: 0,
          backgroundColor: '#FFFFFF',
        },
        '& .RaLayout-content': {
          backgroundColor: '#F9FAFB',
          padding: { xs: '8px', sm: '16px', md: '24px' },
          paddingTop: { xs: '8px', sm: '16px', md: '24px' },
          maxWidth: '100vw',
          overflowX: 'hidden',
          boxSizing: 'border-box',
        },
        '& .RaSidebar-fixed': {
          position: 'fixed',
          top: { xs: '56px', sm: '64px' },
          left: 0,
          height: { xs: 'calc(100vh - 56px) !important', sm: 'calc(100vh - 64px) !important' },
          display: 'flex',
          flexDirection: 'column',
          zIndex: 100,
        },
        '& .MuiDrawer-paper, & .RaSidebar-paper': {
          position: 'relative !important',
          height: '100% !important',
          minHeight: '100% !important',
          display: 'flex',
          flexDirection: 'column',
          backgroundColor: '#FFFFFF !important',
          overflowX: 'hidden',
          transition: 'width 0.2s ease-in-out',
        },
        '& .RaMenu-open': {
          width: { xs: 240, sm: 260 },
          backgroundColor: '#FFFFFF',
          borderRight: '1px solid #E0E0E0',
          boxShadow: '2px 0 8px rgba(0,0,0,0.04)',
          display: 'flex',
          flexDirection: 'column',
          marginTop: 0,
          height: '100%',
          flex: 1,
          '& > ul': {
            display: 'flex',
            flexDirection: 'column',
            height: '100%',
            flex: 1,
          },
        },
        '& .RaMenuItemLink-root': {
          borderRadius: '8px',
          margin: '4px 8px',
          padding: { xs: '8px 12px', md: '8px 16px' },
          fontSize: { xs: '0.875rem', md: '1rem' },
          '&:hover': {
            backgroundColor: '#F9FAFB',
          },
          '&.RaMenuItemLink-active': {
            backgroundColor: '#7A68A7',
            color: '#FFFFFF',
            '& .MuiSvgIcon-root': {
              color: '#FFFFFF',
            },
            '& .lucide': {
              color: '#FFFFFF',
            }
          },
        },
        '& .MuiPaper-root': {
          borderRadius: { xs: '8px', md: '12px' },
          boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
        },
      }}
    />
  );
};

export default CustomLayout;